import { useEffect, useState } from "react";
import { OrderData, StaffPerformance, CategoryRevenue } from "../types/dashboardTypes";

const parseTip = (tip: any): number => {
  if (tip == null) return 0;
  if (typeof tip === "number") return tip;
  if (typeof tip === "string") {
    // try parse json-like or plain number
    try {
      const parsed = JSON.parse(tip);
      return parseTip(parsed);
    } catch {
      return parseFloat(tip) || 0;
    }
  }
  if (typeof tip === "object") {
    if ("amount" in tip && tip.amount != null) return parseFloat(`${tip.amount}`) || 0;
    if ("value" in tip && tip.value != null) return parseFloat(`${tip.value}`) || 0;
    // find numeric-ish property
    const v = Object.values(tip).find((x) => typeof x === "number" || (typeof x === "string" && !isNaN(parseFloat(x as string))));
    return v ? parseFloat(`${v}`) || 0 : 0;
  }
  return 0;
};

export const useStatistics = (orders: OrderData[]) => {
  const [staffPerformance, setStaffPerformance] = useState<StaffPerformance[]>([]);
  const [topServices, setTopServices] = useState<{ name: string; count: number }[]>([]);
  const [categoryRevenue, setCategoryRevenue] = useState<CategoryRevenue[]>([]);

  useEffect(() => {
    if (!orders || orders.length === 0) {
      setStaffPerformance([]);
      setTopServices([]);
      setCategoryRevenue([]);
      return;
    }

    // Top services
    const serviceCount: Record<string, number> = {};
    orders.forEach((order) => {
      (order.Products || []).forEach((product) => {
        const name = product?.name ?? "Unknown";
        serviceCount[name] = (serviceCount[name] || 0) + 1;
      });
    });

    const topServicesData = Object.entries(serviceCount)
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 5);
    setTopServices(topServicesData);

    // Staff performance
    const staffMap: Record<string, { totalSales: number; totalOrders: number; totalTips: number }> = {};

    orders.forEach((order) => {
      const staffname = order?.staffname ?? "Unassigned";
      if (!staffMap[staffname]) {
        staffMap[staffname] = { totalSales: 0, totalOrders: 0, totalTips: 0 };
      }

      const totalAmt = parseFloat((order.totalAmount as any) || "0") || 0;
      staffMap[staffname].totalSales += totalAmt;
      staffMap[staffname].totalOrders += 1;

      if (order.tips) {
        staffMap[staffname].totalTips += parseTip(order.tips);
      }
    });

    const staffPerformanceData: StaffPerformance[] = Object.entries(staffMap)
      .map(([staffname, stats]) => ({ staffname, ...stats }))
      .sort((a, b) => b.totalSales - a.totalSales);

    setStaffPerformance(staffPerformanceData);

    // Category revenue
    const catMap: Record<string, number> = {};
    orders.forEach((order) => {
      (order.Products || []).forEach((product) => {
        const category = (product?.name || "Other").split(" - ")[0] || "Other";
        const price = parseFloat((product?.totalPrice as any) || "0") || 0;
        catMap[category] = (catMap[category] || 0) + price;
      });
    });
    const categoryData: CategoryRevenue[] = Object.entries(catMap).map(([category, revenue]) => ({ category, revenue }));
    setCategoryRevenue(categoryData);
  }, [orders]);

  return { staffPerformance, topServices, categoryRevenue };
};
